import { useState, useEffect, useRef } from 'react';
import { Github, Linkedin, Code2, Mail, MapPin, ArrowRight } from 'lucide-react';
import sam from '../sam.jpg';

const ROLES = ['Full Stack Developer', 'Tech Mentor'];

const STATS = [
  { value: '10+', label: 'Projects delivered' },
  { value: '250+', label: 'Students trained' },
  { value: '2+ yrs', label: 'Experience' },
  { value: '50+', label: 'Webinars conducted' },
  { value: '500+', label: 'Sessions handled' },
];

const CHIPS = [
  { label: 'React', className: 'top-6 -left-6 lg:-left-10', delay: 'animate-float' },
  { label: 'Node.js', className: 'top-1/3 -right-8 lg:-right-14', delay: 'animate-float-slow' },
  { label: 'MongoDB', className: 'bottom-16 -left-10 lg:-left-16', delay: 'animate-float-slower' },
  { label: 'Tailwind', className: 'bottom-4 right-2 lg:right-0', delay: 'animate-float-slow' },
];

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const roleIndex = useRef(0);
  const charIndex = useRef(0);
  const isDeleting = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const tick = () => {
      const currentRole = ROLES[roleIndex.current];

      if (!isDeleting.current) {
        charIndex.current += 1;
        setDisplayText(currentRole.slice(0, charIndex.current));

        if (charIndex.current === currentRole.length) {
          isDeleting.current = true;
          return 1400;
        }
        return 70;
      } else {
        charIndex.current -= 1;
        setDisplayText(currentRole.slice(0, charIndex.current));

        if (charIndex.current === 0) {
          isDeleting.current = false;
          roleIndex.current = (roleIndex.current + 1) % ROLES.length;
          return 400;
        }
        return 35;
      }
    };

    let timeoutId;
    const loop = () => {
      const delay = tick();
      timeoutId = setTimeout(loop, delay);
    };
    timeoutId = setTimeout(loop, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen pt-16 overflow-hidden bg-[#FAFAF9] font-body"
    >
      {/* Dot grid background, faded toward the edges */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(circle, #C4B5FD 1.4px, transparent 1.4px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 60% 55% at 50% 40%, black 40%, transparent 90%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 60% 55% at 50% 40%, black 40%, transparent 90%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center min-h-[calc(100vh-4rem)]">
        {/* Left: copy */}
        <div
          className={`transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-violet-50 border border-violet-100 text-violet-700 text-sm font-medium mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Open to full-time roles
          </div>

          <p className="text-slate-500 text-lg mb-1">Hi, I'm</p>
          <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-[3.75rem] leading-[1.05] tracking-tight text-slate-900 mb-4">
            Shanmuga Priya
          </h1>

          <div className="h-9 flex items-center mb-6">
            <span className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">
              {displayText}
            </span>
            <span className="w-[3px] h-6 bg-pink-600 ml-1 animate-blink" />
          </div>

          <p className="text-slate-600 text-lg leading-relaxed max-w-lg mb-8">
            I build scalable web apps with React and Node, and spend my days
            at GUVI Geek Network helping the next generation of developers
            learn to code — from their first "Hello World" to their first
            real project.
          </p>

          <div className="flex flex-wrap gap-4 mb-10">
            <button
              onClick={() => scrollToSection('projects')}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white bg-gradient-to-r from-violet-600 to-pink-600 hover:shadow-lg hover:shadow-violet-300 transition-all"
            >
              View My Work
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="px-6 py-3 rounded-full font-semibold text-slate-700 border border-slate-200 hover:border-violet-300 hover:text-violet-700 transition-colors"
            >
              Get In Touch
            </button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-5 mb-8 pt-6 border-t border-slate-100">
            {STATS.map((stat) => (
              <div key={stat.label}>
                <p className="font-display text-2xl font-bold text-slate-900">
                  {stat.value}
                </p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Socials */}
          <div className="flex items-center gap-4">
            {[
              { icon: Github, href: 'https://github.com/shanmugapriya1203', label: 'GitHub' },
              { icon: Linkedin, href: 'https://www.linkedin.com/in/shanmuga-priya-661935188/', label: 'LinkedIn' },
              { icon: Code2, href: 'https://leetcode.com/_sam1203/', label: 'LeetCode' },
              { icon: Mail, href: 'mailto:priyamuthukumar1203@gmail.com', label: 'Email' },
            ].map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="p-2.5 rounded-full border border-slate-200 text-slate-500 hover:text-violet-700 hover:border-violet-300 transition-colors"
              >
                <Icon size={19} />
              </a>
            ))}
            <div className="flex items-center gap-1.5 text-sm text-slate-400 ml-2">
              <MapPin size={15} />
              Pudukkottai, Tamil Nadu
            </div>
          </div>
        </div>

        {/* Right: photo in editor-style frame with floating tech chips */}
        <div
          className={`relative flex justify-center lg:justify-end transition-all duration-700 ease-out delay-150 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="absolute -inset-10 bg-gradient-to-br from-violet-200/50 via-pink-200/40 to-transparent rounded-[3rem] blur-3xl" />

          <div className="relative w-full max-w-sm">
            {/* Editor chrome frame */}
            <div className="relative rounded-2xl bg-white shadow-xl shadow-slate-200/70 border border-slate-100 overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-slate-50/80">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                <span className="ml-3 text-xs text-slate-400 font-mono">
                  profile.jsx
                </span>
              </div>
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={sam}
                  alt="Shanmuga Priya"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Floating tech chips */}
            {CHIPS.map((chip) => (
              <div
                key={chip.label}
                className={`hidden sm:flex absolute ${chip.className} ${chip.delay} items-center gap-2 px-3.5 py-2 rounded-xl bg-white shadow-md shadow-slate-200/70 border border-slate-100 text-sm font-medium text-slate-700`}
              >
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-pink-500" />
                {chip.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}